/**
 * Encryption utility functions
 */

/**
 * Check if Web Crypto API is available and secure context
 * @returns {boolean} True if crypto API is available in a secure context
 */
export function isCryptoAvailable () {
	// Crypto API requires secure context (HTTPS or localhost)
	const isSecureContext = window.isSecureContext !== false
	return isSecureContext && typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function'
}

/**
 * Generate a random Base64-encoded key of specified byte length
 * @param {number} byteLength - Number of bytes (32 for AES-256)
 * @returns {string|null} Base64 encoded key or null if crypto unavailable
 */
export function generateBase64Key (byteLength) {
	if (!isCryptoAvailable()) {
		console.warn('Web Crypto API not available')
		return null
	}

	const keyBytes = crypto.getRandomValues(new Uint8Array(byteLength))
	let binary = ''
	for (let i = 0; i < keyBytes.length; i++) {
		binary += String.fromCharCode(keyBytes[i])
	}
	return btoa(binary)
}

/**
 * Validate that a Base64 string is valid and decodes to the expected byte length
 * @param {string} base64Key - The Base64 encoded key
 * @param {number} expectedBytes - Expected decoded length
 * @returns {boolean} True if valid
 */
export function validateBase64KeyLength (base64Key, expectedBytes) {
	if (!base64Key) return false
	try {
		const decoded = atob(base64Key)
		return decoded.length === expectedBytes
	} catch (e) {
		return false
	}
}

/**
 * Validate that a string is valid Base64
 * @param {string} value - The string to validate
 * @returns {boolean} True if valid Base64
 */
export function isValidBase64 (value) {
	if (!value) return false
	try {
		atob(value)
		return true
	} catch (e) {
		return false
	}
}

/**
 * Validate that a string is valid hexadecimal
 * @param {string} value - The string to validate
 * @returns {boolean} True if valid hex (non-empty)
 */
export function isValidHex (value) {
	if (!value) return false
	return /^[0-9a-fA-F]+$/.test(value)
}

/**
 * Validate Salesforce credentials format
 * @param {string} salt - The salt value (hex, min 16 chars)
 * @param {string} iv - The IV value (hex, exactly 32 chars)
 * @returns {Object} Object with saltValid and ivValid booleans
 */
export function validateSalesforceCredentials (salt, iv) {
	return {
		saltValid: isValidHex(salt) && salt.length >= 16,
		ivValid: isValidHex(iv) && iv.length === 32
	}
}

/**
 * Encryption type constants
 */
export const ENCRYPTION_TYPES = {
	AES: 1,
	SALESFORCE: 2
}

export default {
	isCryptoAvailable: isCryptoAvailable,
	generateBase64Key: generateBase64Key,
	validateBase64KeyLength: validateBase64KeyLength,
	isValidBase64: isValidBase64,
	isValidHex: isValidHex,
	validateSalesforceCredentials: validateSalesforceCredentials,
	ENCRYPTION_TYPES: ENCRYPTION_TYPES
}
